// ─── Écran COMPTES : soldes, relevés et transferts internes ───────
// Les comptes disent d'où vient l'argent. Cet écran répond à « combien
// reste-t-il ? » et propose les virements entre comptes détectés — qu'il ne
// applique JAMAIS d'office (un faux positif sortirait de vraies dépenses des
// totaux sans qu'on le voie).
//
// C'est ICI que vit l'IMPORT de relevé (déplacé de « Budget & finances » le
// 10/08, demande de Cédric) : un relevé importé décrit des mouvements de
// COMPTE, pas une intention budgétaire. Et taper un compte ouvre son relevé.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import {
  PageHeader, EmptyState, LigneSwipe, SelecteurDate, frJMA,
  useUI, useT, useAuth, useEspace, confirmerSuppression, confirmerChoix,
  usePileRetour,
} from '@mop/core';
import { ecouterBudgets, ecouterOperations, modifierOperation, supprimerOperation } from './services/budget';
import { ecouterConfigFinance } from './services/configFinance';
import { definirCategoriesPerso } from './data/categories';
import {
  ecouterComptes, creerCompte, modifierCompte, supprimerCompte, soldeCompte, emojiCompte,
} from './services/comptes';
import { soldeCompteA } from './services/releveCompte';
import { lendemainISO } from './services/releveBancaire';
import { detecterTransferts } from './services/transferts';
import { formatEuro, montantOperation, dateEffective } from './services/calculs';
import { cleErreur } from './services/erreurs';
import { ecouterLignes, ecouterExceptions } from './services/lignes';
import { ecouterRattachements, rattacher as creerRattachement, modifierRattachement, detacher, detacherTousDe } from './services/rattachements';
import { prefSession, definirPrefSession } from './services/prefsSession';
import FeuilleCompte from './FeuilleCompte';
import FeuilleOperation from './FeuilleOperation';
import FeuilleImport from './FeuilleImport';
import FicheOperation from './FicheOperation';
// L'ancienne `FeuilleRattacher` (choix d'une ligne, sans montant ni mémoire)
// est remplacée par la feuille COMMUNE aux deux sens — Cédric le 11/08 :
// « cette modale devrait être identique à celle dont on parlait juste avant ».
// Remplacée le 15/08 par la MULTI-sélection en arbre (FeuilleRattacherLignes) :
// « sélectionner une à plusieurs lignes de budget et mettre le % ou le
// montant de chaque ligne » — Cédric.
import FeuilleRattacherLignes from './FeuilleRattacherLignes';
import EcranCompte from './EcranCompte';

export default function EcranComptes() {
  const UI = useUI();
  const t = useT();
  const { user } = useAuth();
  const { espaceActif, tenantId, assurerEspace } = useEspace();
  const s = useMemo(() => makeStyles(UI), [UI]);

  const [comptes, setComptes] = useState([]);
  const [operations, setOperations] = useState([]);
  const [feuille, setFeuille] = useState(null);
  const [dateReq, setDateReq] = useState(null);
  // Config finance de l'espace : categories perso, masquages, sous-cats.
  const [configCategories, setConfigCategories] = useState(null);
  const [ouvert, setOuvert] = useState(null);        // relevé d'un compte
  const [importOuvert, setImportOuvert] = useState(false);
  const [feuilleO, setFeuilleO] = useState(null);    // { initial } opération éditée
  const [fiche, setFiche] = useState(null);          // opération en LECTURE
  const [aRattacher, setARattacher] = useState(null);
  // La prévision et la charnière : la fiche montre le rattachement dans
  // l'autre sens — ce que l'opération honore, et pour quelle part.
  const [lignes, setLignes] = useState([]);
  const [rattachements, setRattachements] = useState([]);
  // Les annotations de mois : sans elles, une occurrence soldée ou ignorée
  // serait proposée comme cible alors qu'elle ne réclame plus rien.
  const [exceptions, setExceptions] = useState([]);
  // Pour nommer l'enveloppe de chaque ligne candidate au rattachement.
  const [budgets, setBudgets] = useState([]);
  const [erreur, setErreur] = useState('');
  // D'où l'on vient, pour y revenir à la fermeture (Cédric, 13/08) :
  // ouvrir un écran depuis un autre oblige à FERMER le premier (deux
  // <Modal> imbriquées figent l'écran), sinon on retombe sur la liste.
  const retour = usePileRetour();
  const signaler = (quoi) => (e) => setErreur(`${quoi} — ${e?.message || e?.code || 'erreur'}`);
  // La SYNTHÈSE se plie depuis l'en-tête (16/08) — mémorisé pour la session.
  const [syntheseVisible, setSyntheseVisible] = useState(() => prefSession('syntheseComptes', true));
  const basculerSynthese = () => setSyntheseVisible(v => {
    definirPrefSession('syntheseComptes', !v);
    return !v;
  });

  useEffect(() => ecouterComptes(tenantId, setComptes, signaler('comptes')), [tenantId]);
  useEffect(() => ecouterConfigFinance(tenantId, (c) => {
    setConfigCategories(c);
    // Le registre d'affichage : les emojis des categories perso, partout.
    definirCategoriesPerso(c.categoriesPerso);
  }), [tenantId]);
  useEffect(() => ecouterOperations(tenantId, setOperations, signaler('operations')), [tenantId]);
  useEffect(() => ecouterLignes(tenantId, setLignes, signaler('lignesBudget')), [tenantId]);
  useEffect(() => ecouterBudgets(tenantId, setBudgets, signaler('budgets')), [tenantId]);
  useEffect(() => ecouterRattachements(tenantId, setRattachements, signaler('rattachements')), [tenantId]);
  useEffect(() => ecouterExceptions(tenantId, setExceptions, signaler('occurrencesBudget')), [tenantId]);

  // Les motifs de rattachement deviennent des phrases : « rattachement.
  // montantNul » affiche tel quel est un code interne presente comme un
  // message (Cedric, 15/08). Un motif inconnu retombe sur le brut.
  const echec = (e) => {
    const cle = cleErreur(e);
    Alert.alert(t('commun.erreur'), cle ? t(cle) : (e?.message || ''));
  };
  const nomCompte = (id) => (comptes.find(c => c.id === id) || {}).nom || '';

  // Membres de l'espace : l'édition d'une opération demande de savoir qui paie.
  const membres = useMemo(() => {
    const e = espaceActif || {};
    return (e.membreUids || []).map(uid => ({
      uid,
      nom: (e.noms || {})[uid] || (uid === user?.uid ? (user?.prenom || user?.email || '') : uid.slice(0, 5)),
    }));
  }, [espaceActif, user]);

  const compteOuvert = ouvert ? comptes.find(c => c.id === ouvert) : null;

  // Une part par ligne cochée, en SÉQUENCE : chaque écriture doit voir les
  // précédentes — en parallèle, le contrôle du dépassement lirait une liste
  // de rattachements périmée.
  const rattacherParts = async (parts) => {
    try {
      const faits = [];
      for (const p of parts) {
        const id = await creerRattachement({
          depense: aRattacher, rattachements: [...rattachements, ...faits], espaceId: tenantId,
          ligneId: p.occurrence.ligneId, mois: p.occurrence.mois,
          montant: p.montant, origine: 'manuel', creePar: user?.uid,
        });
        faits.push({ id, depenseId: aRattacher.id,
          ligneId: p.occurrence.ligneId, mois: p.occurrence.mois, montant: p.montant });
      }
    } catch (e) { echec(e); }
  };

  // Mouvement INTERNE : l'opération sort des totaux sans être supprimée, et
  // ses parts sont retirées — même règle que dans le Suivi. Réversible.
  const marquerInterne = (op, exclu) =>
    modifierOperation(op.id, { exclu, ...(exclu ? { budgetId: op.budgetId || null } : {}) }, op)
      .then(() => (exclu ? detacherTousDe({ espaceId: tenantId, depenseId: op.id }) : null))
      .catch(echec);

  // RAPPROCHEMENT BANCAIRE (16/08) : après un import, comparer le solde que
  // l'app calcule à celui que la banque AFFIRME (LEDGERBAL du relevé). Un
  // écart est signalé, et le recalage PROPOSÉ — jamais appliqué d'office.
  // L'ancre repart du lendemain : le solde de la banque est celui d'APRÈS
  // les opérations du jour.
  const rapprocher = (r) => {
    if (!r?.soldeBanque || !r.compteId) return;
    const compte = comptes.find(c => c.id === r.compteId);
    if (!compte) return;
    const calcule = soldeCompteA(compte, operations, r.soldeBanque.date);
    const ecart = Math.round((r.soldeBanque.montant - calcule) * 100) / 100;
    if (Math.abs(ecart) < 0.005) return;   // tout colle : pas de bruit
    confirmerChoix(
      t('finances.rapprochementTitre'),
      t('finances.rapprochementMsg', {
        banque: formatEuro(r.soldeBanque.montant), date: r.soldeBanque.date,
        calcule: formatEuro(calcule), ecart: (ecart > 0 ? '+' : '') + formatEuro(ecart),
      }),
      [
        { text: t('finances.recalerSolde'), onPress: () => modifierCompte(compte.id, {
          soldeInitial: r.soldeBanque.montant, dateSolde: lendemainISO(r.soldeBanque.date),
        }).catch(echec) },
        { text: t('commun.annuler'), style: 'cancel' },
      ],
    );
  };

  // Paires proposées : on n'affiche que celles qui restent à traiter.
  const transferts = useMemo(() => detecterTransferts(operations), [operations]);

  const enregistrer = async (c) => {
    try {
      if (feuille?.initial?.id) await modifierCompte(feuille.initial.id, c);
      else {
        const espaceId = tenantId || await assurerEspace();
        await creerCompte({ ...c, espaceId, creePar: user?.uid });
      }
      setFeuille(null);
    } catch (e) { echec(e); }
  };

  // Confirmer un transfert = sortir LES DEUX côtés des totaux, d'un seul geste.
  const confirmerTransfert = async (p) => {
    try {
      await modifierOperation(p.depuis.id, { exclu: true }, p.depuis);
      await modifierOperation(p.vers.id, { exclu: true }, p.vers);
    } catch (e) { echec(e); }
  };

  const total = comptes.reduce((s2, c) => s2 + soldeCompte(c, operations), 0);

  // Donnée VIVANTE et non le cliché pris à l'ouverture : après une édition,
  // Firestore met la liste à jour mais pas l'objet gardé en état, et la fiche
  // affichait encore les anciennes valeurs (Cédric, 13/08).
  const ficheVive = useMemo(
    () => (fiche ? operations.find(o => o.id === fiche.id) || fiche : null),
    [fiche, operations],
  );

  return (
    <View style={{ flex: 1, backgroundColor: UI.bg }}>
      <PageHeader titre={t('finances.comptes')} iconeTitre="🏦"
        action={{ icone: '＋', label: t('finances.nouveauCompte'), onPress: () => setFeuille({ initial: null }) }}>
        {/* La bascule de SYNTHÈSE (16/08) : grisée quand la carte est cachée. */}
        <TouchableOpacity onPress={basculerSynthese} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel={t('finances.basculerSynthese')}>
          <Text style={{ fontSize: 19, marginLeft: 14, opacity: syntheseVisible ? 1 : 0.35 }}>📊</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setImportOuvert(true)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityLabel={t('budget.importer')}>
          <Text style={{ fontSize: 20, marginLeft: 14 }}>📥</Text>
        </TouchableOpacity>
      </PageHeader>

      <ScrollView contentContainerStyle={s.corps}>
        {!!erreur && (
          <TouchableOpacity style={s.erreur} activeOpacity={0.8} onPress={() => setErreur('')}>
            <Text style={s.erreurTxt}>⚠️ {erreur}</Text>
          </TouchableOpacity>
        )}

        {comptes.length === 0 ? (
          <EmptyState icon="🏦" texte={t('finances.aucunCompte')} aide={t('finances.aucunCompteAide')} />
        ) : (
          <>
            {syntheseVisible && (
              <View style={s.totalCarte}>
                <Text style={s.totalLbl}>{t('finances.totalComptes')}</Text>
                <Text style={[s.totalVal, total < 0 && { color: UI.danger || '#c0392b' }]}>{formatEuro(total)}</Text>
              </View>
            )}

            {comptes.map(c => {
              const solde = soldeCompte(c, operations);
              const nb = operations.filter(o => o.compteId === c.id).length;
              return (
                <LigneSwipe key={c.id} espacement={8} enabled
                  onEditer={() => setFeuille({ initial: c })}
                  onSupprimer={() => confirmerSuppression(t('commun.supprimer'), c.nom,
                    () => supprimerCompte(c.id, tenantId).catch(echec))}>
                  {/* Tap = OUVRIR le relevé (convention de l'app : le tap
                      informe, le swipe → modifie). L'édition reste donc
                      accessible, elle a seulement changé de geste. */}
                  <TouchableOpacity style={s.carte} activeOpacity={0.7} onPress={() => setOuvert(c.id)}>
                    <Text style={{ fontSize: 22 }}>{emojiCompte(c)}</Text>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={s.nom} numberOfLines={1}>{c.nom}</Text>
                      <Text style={s.sous} numberOfLines={1}>
                        {c.banque ? `${c.banque}  ·  ` : ''}{c.fin4 ? `••${c.fin4}  ·  ` : ''}{nb}
                      </Text>
                    </View>
                    <Text style={[s.solde, solde < 0 && { color: UI.danger || '#c0392b' }]}>{formatEuro(solde)}</Text>
                  </TouchableOpacity>
                </LigneSwipe>
              );
            })}
          </>
        )}

        {/* Virements entre comptes : proposés, jamais appliqués d'office. */}
        {transferts.length > 0 && (
          <>
            <Text style={s.titre}>⇄ {t('finances.transfertsDetectes')}  ·  {transferts.length}</Text>
            <Text style={s.aide}>{t('finances.transfertsAide')}</Text>
            {transferts.map(p => (
              <View key={p.cle} style={s.transfert}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={s.trTxt} numberOfLines={1}>
                    {nomCompte(p.depuis.compteId)} → {nomCompte(p.vers.compteId)}
                  </Text>
                  <Text style={s.sous} numberOfLines={1}>
                    {frJMA(dateEffective(p.depuis))}
                    {p.jours > 0 ? `  ·  ${p.jours} j` : ''}
                    {'  ·  '}{p.depuis.libelle || ''}
                  </Text>
                </View>
                <Text style={s.trMontant}>{montantOperation(p.montant, 'depense')}</Text>
                <TouchableOpacity style={s.btn} activeOpacity={0.7} onPress={() => confirmerTransfert(p)}>
                  <Text style={s.btnTxt}>⊘</Text>
                </TouchableOpacity>
              </View>
            ))}
          </>
        )}
      </ScrollView>

      {/* Le relevé est un CALQUE, et les feuilles sont rendues APRÈS lui : une
          feuille rendue à l'intérieur se coincerait sous le calque. */}
      {!!compteOuvert && (
        <View style={[StyleSheet.absoluteFill, { zIndex: 50, elevation: 30 }]}>
          <EcranCompte compte={compteOuvert} operations={operations}
            onFermer={() => setOuvert(null)}
            onFiche={setFiche}
            rattachements={rattachements}
            lignes={lignes} budgets={budgets}
            onEditer={(o) => setFeuilleO({ initial: o })}
            onSupprimer={(o) => supprimerOperation(o.id, o).catch(echec)} />
        </View>
      )}

      <FeuilleCompte visible={!!feuille} initial={feuille?.initial} onDemanderDate={setDateReq}
        onEnregistrer={enregistrer} onFermer={() => setFeuille(null)} />
      <FeuilleOperation configCategories={configCategories} visible={!!feuilleO} initial={feuilleO?.initial} membres={membres} monUid={user?.uid}
        onDemanderDate={setDateReq}
        onEnregistrer={async (o) => {
          try { await modifierOperation(feuilleO.initial.id, o, feuilleO.initial); setFeuilleO(null); retour.revenir(); }
          catch (e) { echec(e); }
        }}
        onFermer={() => { setFeuilleO(null); retour.revenir(); }} />
      {/* Tap sur une opération = sa FICHE, d'où l'on édite, détache une part
          ou en rattache une nouvelle. C'est le rattachement vu depuis le
          COMPTE — l'autre sens de celui qu'on voit depuis la prévision. */}
      <FicheOperation visible={!!fiche} operation={ficheVive} rattachements={rattachements} lignes={lignes}
        budgets={budgets}
        nomCompte={(comptes.find(c => c.id === fiche?.compteId) || {}).nom || ''}
        onFermer={() => { setFiche(null); retour.revenir(); }}
        onEditer={() => { const o = ficheVive; retour.empiler(() => setFiche(o)); setFeuilleO({ initial: o }); setFiche(null); }}
        onDetacher={(r) => detacher(r.id).catch(echec)}
        /* Modifier la PART d'un rattachement (16/08) : le contrôle du
           dépassement relit l'opération et les autres parts. */
        onModifierPart={(r, montant) => modifierRattachement(r.id, {
          depense: ficheVive, rattachements, ligneId: r.ligneId, mois: r.mois, montant,
        }).catch(echec)}
        onRattacher={(op) => { const o = fiche; retour.empiler(() => setFiche(o)); setARattacher(op); setFiche(null); }} />

      {/* L'arbre par enveloppe est desormais une VUE de l'ecran du compte
          (bascule 🌳/📅), plus une modale — Cedric, 15/08. */}

      {/* MULTI-sélection en arbre (15/08) : tous les budgets du mois avec
          leurs lignes, une part par ligne cochée. */}
      <FeuilleRattacherLignes visible={!!aRattacher} operation={aRattacher}
        lignes={lignes} budgets={budgets} operations={operations}
        exceptions={exceptions} rattachements={rattachements}
        onValider={(parts) => { rattacherParts(parts); setARattacher(null); retour.revenir(); }}
        /* « Non visible » aussi depuis les Comptes (Cédric, 16/08) : l'option
           n'existait que par la porte du Suivi. */
        onInterne={(op, exclu) => { marquerInterne(op, exclu); setARattacher(null); retour.revenir(); }}
        onFermer={() => { setARattacher(null); retour.revenir(); }} />

      <FeuilleImport visible={importOuvert} espaceId={tenantId} user={user} comptes={comptes}
        onImporte={rapprocher}
        onFermer={() => setImportOuvert(false)} />
      <SelecteurDate visible={!!dateReq} valeur={dateReq?.valeur} avecEffacer
        onChange={(dk) => dateReq?.onChoisir(dk)} onFermer={() => setDateReq(null)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  corps: { padding: 12, paddingBottom: 30 },
  totalCarte: { backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13, marginBottom: 12 },
  totalLbl: { fontSize: 11, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },
  totalVal: { fontSize: 21, fontWeight: '800', color: UI.ink, marginTop: 2 },
  carte: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: UI.card, borderRadius: 14, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  nom: { fontSize: 15, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 12, color: UI.muted, marginTop: 2 },
  solde: { fontSize: 15, fontWeight: '800', color: UI.ink },
  titre: { fontSize: 12, color: UI.faint, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: 18, marginBottom: 4 },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginBottom: 8 },
  transfert: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 10, paddingHorizontal: 12, marginBottom: 8 },
  trTxt: { fontSize: 14, fontWeight: '700', color: UI.ink },
  trMontant: { fontSize: 14, fontWeight: '800', color: UI.ink },
  btn: { width: 36, height: 36, borderRadius: 10, alignItems: 'center', justifyContent: 'center', borderWidth: 0.5, borderColor: UI.accent },
  btnTxt: { fontSize: 16, color: UI.accent },
  erreur: { backgroundColor: UI.card, borderRadius: 12, borderWidth: 1, borderColor: UI.danger || '#c0392b', padding: 11, marginBottom: 10 },
  erreurTxt: { fontSize: 13, color: UI.danger || '#c0392b', fontWeight: '700' },
});
